import React from 'react';
import { LuChevronLeft } from 'react-icons/lu';
import { useSession } from '../../context/SessionContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { useChargerBack } from '../../utils/chargerNav.js';
import { STEPS } from '../../constants/steps.js';
import GreenChargeLogo from '../Common/GreenChargeLogo.jsx';
import './EvHeader.less';

/**
 * Wizard header — logo left, action right.
 *
 * On step 1 (EV_PROFILE — the "charger overview" landing) the right-
 * side slot becomes a compact source-aware Back button in place of
 * the `headerAction.label`. Back and label are mutually exclusive:
 * never rendered together, never leaving an empty slot. This
 * replaces the previous floating Back pill on the standalone shell
 * so the affordance now lives INSIDE the charger profile header
 * rather than as a page-level control at the far-left of the
 * viewport.
 *
 * Back visibility is deliberately scoped to EV_PROFILE — once the
 * customer commits with "Get Started" the wizard's own back/continue
 * bar is authoritative and a second Back here would be confusing.
 */
const EvHeader = ({
    headerAction = {
        label: 'EV Charger App',
        action: () => {},
    },
}) => {
    const session = useSession();
    const step = session?.state?.step;
    const { back, isPending } = useChargerBack();
    // Single source of truth for auth state — the same AuthContext the
    // rest of the app already reads (`user` object hydrated from GET
    // /auth/me after a valid access token). Anonymous QR-scan visitors
    // to /station have nowhere sensible to go "back" to inside the
    // app; the standalone flow IS their entry point. Render the
    // control only when we've positively established an authenticated
    // session — never optimistically while the context is still
    // hydrating, so a cold-load auth'd user never sees a flash of
    // wrong state either way.
    //
    // Deliberately NOT gated on `user` truthy (the object could
    // hydrate partially or reference a stale local snapshot), NOT on
    // `session` (SessionContext is the CHARGING session, not the auth
    // session — it exists for every anonymous visitor too), NOT on
    // token presence alone (a stale token whose /auth/me call has not
    // yet failed the interceptor is not "authenticated"). Only the
    // context-owned `isAuthenticated` flag, combined with a NOT-booting
    // guard, satisfies "positively established" in every state.
    const { isAuthenticated, isBooting: authBooting } = useAuth();
    const showBack = step === STEPS.EV_PROFILE && isAuthenticated && !authBooting;

    return (
        <header className="header">
            {/* Theme-aware wordmark. The inline flex values let it yield to
                the title on the narrowest phones (see EvHeader.less). */}
            <GreenChargeLogo
                className="header-logo"
                style={{ flexShrink: 1, minWidth: 0 }}
            />
            {showBack ? (
                <button
                    type="button"
                    className="header-back"
                    onClick={back}
                    disabled={isPending}
                    aria-label="Back"
                >
                    <LuChevronLeft aria-hidden="true" />
                    <span>Back</span>
                </button>
            ) : (
                <h2 onClick={headerAction.action} className="text-muted">
                    {headerAction.label}
                </h2>
            )}
        </header>
    );
};

export default EvHeader;
